// Main JavaScript entry point
import './utils/router.js';
import { setFeatureFlags } from './utils/feature-flags.js';
import { isGroupInviteRoute } from './utils/group-invite-route.js';
import { isEmailVerificationRoute } from './utils/email-verification-route.js';
import { getFullCurrentPath } from './utils/return-to-url.js';
import { isLandingRoute, isPublicRoute } from './utils/auth-routes.js';

import './components/activity-panel.js';
import './components/add-friend-cta.js';
import './components/home-promo-banner.js';
import './components/admin-dashboard.js';
import './components/admin-settings-page.js';
import './components/admin-feature-flags.js';
import './components/audit-logs.js';
import './components/auth-modal.js';
import './components/auth-status.js';
import './components/blocked-domains.js';
import './components/comments-section.js';
import './components/event-comments.js';
import './components/event-detail.js';
import './components/event-edit.js';
import './components/event-invite-modal.js';
import './components/events-calendar.js';
import './components/events-list.js';
import './components/facebook-import-friends.js';
import './components/facebook-import-posts.js';
import './components/facebook-import.js';
import './components/friend-requests.js';
import './components/friends-list.js';
import './components/google-drive-picker.js';
import './components/google-photos-picker.js';
import './components/add-group-cta.js';
import './components/groups-hub.js';
import './components/group-create.js';
import './components/group-shell.js';
import './components/group-add-thread-modal.js';
import './components/group-settings-page.js';
import './components/group-settings-members.js';
import './components/group-settings-rss.js';
import './components/group-settings-moderation.js';
import './components/group-invite-modal.js';
import './components/group-invite-accept-page.js';
import './components/mascot-parade-page.js';
import './components/guest-rsvp.js';
import './components/image-flags.js';
import './components/widget-flags.js';
import './components/imported-friends.js';
import './components/invitations-list.js';
import './components/invite-friend-modal.js';
import './components/landing-page.js';
import './components/main-content.js';
import './components/markdown-preview.js';
import './components/markdown-toolbar.js';
import './components/media-attachment.js';
import './components/media-source-picker.js';
import './components/media-sources-settings.js';
import './components/memories.js';
import './components/mood-selector.js';
import './components/music-player.js';
import './components/music-selector.js';
import './components/post-type-selector.js';
import './components/edit-poll.js';
import './components/view-poll.js';
import './components/not-found.js';
import './components/notification-settings.js';
import './components/oauth-authorize.js';
import './components/oauth-clients.js';
import './components/photocopy-icon.js';
import './components/post-detail.js';
import './components/post-item.js';
import './components/template-editor.js';
import './components/collapsible-section.js';
import './components/post-style-picker.js';
import './components/layout-component-picker-modal.js';
import './components/layout-split-editor.js';
import './components/layout-split-view.js';
import './components/layout-grid-surface.js';
import './components/fot-widget.js';
import './components/widget-flag-picker-modal.js';
import './components/widget-demo-page.js';
import './components/post-writer.js';
import './components/feed-filters.js';
import './components/feed-filters-modal.js';
import './components/posts-feed.js';
import './components/privacy-policy.js';
import './components/prompt-management.js';
import './components/widget-management.js';
import './components/admin-widget-management.js';
import './components/prompt-picker.js';
import './components/protected-component.js';
import './components/release-notes.js';
import './components/docs-page.js';
import './components/return-nav.js';
import './components/reminder-config.js';
import './components/rss-feeds.js';
import './components/safety-armadillo.js';
import './components/settings-page.js';
import './components/site-footer.js';
import './components/site-stats.js';
import './components/taco-menu.js';
import './components/terms-conditions.js';
import './components/theme-toggle.js';
import './components/toolbar-action-bar.js';
import './components/toolbar-overflow-actions.js';
import './components/two-factor-settings.js';
import './components/upcoming-events-banner.js';
import './components/user-management.js';
import './components/user-mention.js';
import './components/user-profile.js';
import './components/washing-machine-icon.js';

// Note: All route-specific components are now loaded lazily via component-loader
// when their routes are accessed. This includes:
// - post-writer, posts-feed, post-detail, comments-section, post-item
// - friends-list, friend-requests, invitations-list
// - user-profile, admin-dashboard, settings-page
// - events-list, events-calendar, event-detail, event-edit
// - memories, and all other route-specific components
//
// Sub-components (like markdown-toolbar, mood-selector, etc.) are imported
// by their parent components and will be loaded automatically when needed.

// timezone setup
dayjs.extend(window.dayjs_plugin_utc);
dayjs.extend(window.dayjs_plugin_timezone);
dayjs.extend(window.dayjs_plugin_localizedFormat);

const PUBLIC_INFO_ROUTES = {
  '/privacy': 'privacy',
  '/terms': 'terms',
  '/release-notes': 'release-notes'
};

// Enhanced navigation with clean URLs
class Navigation {
  constructor() {
    this.init();
  }

  init() {
    // Navigation is now handled by the router
    // This class can handle additional navigation-specific logic

    // Update navigation when auth state changes
    document.addEventListener('auth-status-loaded', (e) => {
      const { authenticated } = e.detail;
      this.updateNavigationForAuth(authenticated);
    });

    // Handle logout navigation
    document.addEventListener('user-logged-out', () => {
      // Redirect to login page
      window.router.navigate('/login');
    });
  }

  updateNavigationForAuth(isAuthenticated) {
    const nav = document.querySelector('.main-nav');
    if (nav) {
      if (isAuthenticated) {
        nav.style.display = 'block';
      } else {
        nav.style.display = 'none';
      }
    }
  }
}

// Initialize navigation when DOM is loaded
document.addEventListener('DOMContentLoaded', async () => {
  new Navigation();
  // Load auth + feature flags before first route render so gated UI is correct
  await initializeApp();
  window.router.doInitialPageLoad();
});

// Initialize app with authentication check
async function initializeApp() {
  window.logger.info('Initializing Fun Old Times frontend');

  try {
    // Check authentication status and feature flags in parallel
    window.logger.debug('Checking authentication status');
    const [response, featuresResponse] = await Promise.all([
      fetch('/api/auth/status', { credentials: 'include' }),
      fetch('/api/features', { credentials: 'include' })
    ]);

    if (featuresResponse.ok) {
      const featuresData = await featuresResponse.json();
      setFeatureFlags(featuresData.features || {});
      document.dispatchEvent(new CustomEvent('feature-flags-loaded', {
        detail: { features: featuresData.features || {} },
        bubbles: true
      }));
    } else {
      window.logger.warn('Failed to load feature flags', {
        status: featuresResponse.status
      });
    }

    if (response.ok) {
      const data = await response.json();
      const user = data.authenticated ? data.user : null;

      window.logger.auth('status_checked', user?.id, {
        authenticated: !!user,
        username: user?.username
      });

      document.dispatchEvent(new CustomEvent('auth-status-loaded', {
        detail: { user: user, authenticated: !!user },
        bubbles: true
      }));

      if (!data.authenticated) {
        handleUnauthenticatedAccess(window.location.pathname);
      } else {
        // User is authenticated - check if on public info route
        if (isPublicInfoRoute(window.location.pathname)) {
          hideMainContent();
          showPublicPage(resolvePublicPageKey(window.location.pathname));
        } else {
          // Show main content and continue with routing
          showMainContent();
        }
      }
    } else {
      // Error checking auth status, check if on special route
      handleUnauthenticatedAccess(window.location.pathname);
    }
  } catch (error) {
    window.logger.error('Auth check failed', error, {
      path: window.location.pathname,
      url: window.location.href
    });
    handleUnauthenticatedAccess(window.location.pathname);
  } finally {
    hideLoadingScreen();
  }
}

function hideLoadingScreen() {
  const loadingScreen = document.querySelector('#loadingScreen');
  if (loadingScreen) {
    loadingScreen.style.display = 'none';
  }
}

function handleUnauthenticatedAccess(currentPath) {
  if (isLandingRoute(currentPath)) {
    hideMainContent();
    showLandingPage();
    return;
  }

  hideLandingPage();

  if (isPublicInfoRoute(currentPath)) {
    hideMainContent();
    showPublicPage(resolvePublicPageKey(currentPath));
    return;
  }

  hidePublicPage();

  if (isGroupInviteRoute(currentPath)) {
    showMainContent();
    document.dispatchEvent(new CustomEvent('route-changed', {
      detail: { route: currentPath }
    }));
    return;
  }

  if (isEmailVerificationRoute(currentPath)) {
    hideMainContent();
    return;
  }

  if (!isPublicRoute(currentPath)) {
    hideMainContent();
    window.router.redirectToLogin();
    return;
  }

  if (isGuestRoute(currentPath)) {
    showGuestRouteShell();
    return;
  }

  hideMainContent();

  const modalMode = normalizeModalMode(currentPath);
  showAuthModal(modalMode);
}

function isGuestRoute(path) {
  return path.startsWith('/events/guest');
}

function isPublicInfoRoute(path) {
  if (Object.prototype.hasOwnProperty.call(PUBLIC_INFO_ROUTES, path)) {
    return true;
  }
  return path === '/docs' || path.startsWith('/docs/');
}

function resolvePublicPageKey(path) {
  if (path === '/docs' || path.startsWith('/docs/')) {
    return 'docs';
  }
  return PUBLIC_INFO_ROUTES[path] || null;
}

function normalizeModalMode(path) {
  let mode = path;
  if (mode.charAt(0) === '/') {
    mode = mode.slice(1);
  }
  if (mode.charAt(mode.length - 1) === '/') {
    mode = mode.slice(0, -1);
  }
  return mode || 'login';
}

// Show authentication modal
function showAuthModal(mode = 'login', required = false) {
  let authModal = document.querySelector('auth-modal');
  if (!authModal) {
    authModal = document.createElement('auth-modal');
    document.body.appendChild(authModal);
  }
  authModal.open(mode, required);
}

// Hide main content when not authenticated
function hideMainContent() {
  const mainContent = document.querySelector('.main-content');
  const loadingScreen = document.querySelector('#loadingScreen');
  const guestHost = document.getElementById('guest-route-host');
  const primaryContent = document.querySelector('main-content');

  if (mainContent) {
    mainContent.style.display = 'none';
  }

  if (guestHost) {
    guestHost.classList.add('hidden');
    guestHost.innerHTML = '';
  }

  if (primaryContent) {
    primaryContent.style.display = '';
  }

  if (loadingScreen) {
    loadingScreen.style.display = 'none';
  }
}

// Show main content when authenticated
function showMainContent() {
  const mainContent = document.querySelector('.main-content');
  const loadingScreen = document.querySelector('#loadingScreen');
  const guestHost = document.getElementById('guest-route-host');
  const primaryContent = document.querySelector('main-content');
  hideLandingPage();
  hidePublicPage();
  hideLandingPage();

  if (loadingScreen) {
    loadingScreen.style.display = 'none';
  }

  if (guestHost) {
    guestHost.classList.add('hidden');
    guestHost.innerHTML = '';
  }

  if (primaryContent) {
    primaryContent.style.display = '';
  }

  if (mainContent) {
    mainContent.style.display = 'block';
  }
}

function showGuestRouteShell() {
  const mainContent = document.querySelector('.main-content');
  const guestHost = document.getElementById('guest-route-host');
  const primaryContent = document.querySelector('main-content');
  const loadingScreen = document.querySelector('#loadingScreen');

  hideLandingPage();
  hidePublicPage();

  if (loadingScreen) {
    loadingScreen.style.display = 'none';
  }

  if (mainContent) {
    mainContent.style.display = 'block';
  }

  if (primaryContent) {
    primaryContent.style.display = 'none';
  }

  if (guestHost) {
    guestHost.classList.remove('hidden');
  }
}

function showLandingPage() {
  const host = document.getElementById('landingPageHost');
  const appLayout = document.querySelector('.app-layout');

  hidePublicPage();

  if (document.body) {
    document.body.classList.add('landing-visible');
  }

  if (appLayout) {
    appLayout.setAttribute('aria-hidden', 'true');
  }

  if (host) {
    host.classList.remove('hidden');
  }
}

function hideLandingPage() {
  const host = document.getElementById('landingPageHost');
  const appLayout = document.querySelector('.app-layout');

  if (document.body) {
    document.body.classList.remove('landing-visible');
  }

  if (appLayout) {
    appLayout.removeAttribute('aria-hidden');
  }

  if (host && !host.classList.contains('hidden')) {
    host.classList.add('hidden');
  }
}

function showPublicPage(pageKey) {
  const host = document.getElementById('publicPageHost');
  const appLayout = document.querySelector('.app-layout');
  if (!host) {
    return;
  }

  hideLandingPage();

  const componentTag = pageKey === 'privacy' ? 'privacy-policy' :
    pageKey === 'terms' ? 'terms-conditions' :
      pageKey === 'release-notes' ? 'release-notes' :
        pageKey === 'docs' ? 'docs-page' : null;

  if (!componentTag) {
    hidePublicPage();
    return;
  }

  if (document.body) {
    document.body.classList.add('public-page-visible');
  }

  if (appLayout) {
    appLayout.setAttribute('aria-hidden', 'true');
  }

  host.innerHTML = `<${componentTag}></${componentTag}>`;
  host.classList.remove('hidden');
  host.classList.add('active');
}

function hidePublicPage() {
  const host = document.getElementById('publicPageHost');
  const appLayout = document.querySelector('.app-layout');
  if (!host) {
    return;
  }

  if (document.body) {
    document.body.classList.remove('public-page-visible');
  }

  if (appLayout) {
    appLayout.removeAttribute('aria-hidden');
  }

  host.classList.add('hidden');
  host.classList.remove('active');
  host.innerHTML = '';
}

// Handle authentication success
document.addEventListener('auth-success', () => {
  showMainContent();
});

// Handle authentication required events
document.addEventListener('auth-required', async () => {
  hideMainContent();
  window.router.redirectToLogin();
});

// Handle user logout
document.addEventListener('user-logged-out', async () => {
  hideMainContent();
  window.router.navigate('/login');
});

document.addEventListener('landing-auth-request', (event) => {
  const mode = event?.detail?.mode || 'login';
  hideLandingPage();
  hidePublicPage();
  showAuthModal(mode, true);
});

document.addEventListener('public-page-request', (event) => {
  const page = event?.detail?.page;
  if (page && (Object.values(PUBLIC_INFO_ROUTES).includes(page) || page === 'docs')) {
    hideMainContent();
    showPublicPage(page);
  }
});

// Listen for hide-public-page event from router
document.addEventListener('hide-public-page', () => {
  hidePublicPage();
});

// Listen for show-landing-page event from router
document.addEventListener('show-landing-page', () => {
  hidePublicPage();
  hideMainContent();
  showLandingPage();
});

// Expose showMainContent globally so router can call it
window.showMainContent = showMainContent;
